import { type PostQuerySupplierArgs, type ResultSuppliers } from '../../product-router/product/result-provider/result-provider-types';
import type { SearchResult, SearchItems } from '../../product-router/product/result-types';
import { type GenericFilter, SITE_FILTER_TYPE_NAME } from '../../filters/filter-types';
import { useProductContext } from '../../product-router/product-router';
import { type AggResponse, fetchAgg, type EntityATI } from '@atlassian/search-client';
import { dateFormatter } from '../../products/utils/date-formatter';
import {
	JIRA_SEARCH_DIALOG_QUERY,
	JIRA_SEARCH_DIALOG_OPERATION_NAME,
} from './jira-search-dialog-query';
import {
	type JiraSearchData,
	type JiraSearchDialogQueryVariables,
	type JiraExtendedSearchResult,
	type CreateGraphQLSearchSupplierParams,
	type UseJiraGraphQLSupplierParams,
} from './types';
import { type BackendExperiment, getBackendExperiment } from '@atlassian/search-experiment';
import { fg } from '@atlaskit/platform-feature-flags';
import { onGetBackendExperimentFailed } from '../../../common/analytics/events';
import { useAnalytics } from '../../../common/analytics/use-analytics';

const MAX_DEFAULT_SITES = 3;
const USER_ARI_PREFIX = 'ari:cloud:identity::user/';
const HELLO_CLOUD_ID = 'a436116f-02ce-4520-8fbb-7301462a1674';
const QUICK_SEARCH_EXPERIENCE = 'quick-search';

const jiraSearchDialogQuery = async ({
	variables,
}: {
	variables: JiraSearchDialogQueryVariables;
}): Promise<AggResponse<JiraSearchData>> => {
	return fetchAgg<JiraSearchDialogQueryVariables, JiraSearchData>({
		variables,
		graphQuery: JIRA_SEARCH_DIALOG_QUERY,
		operationName: JIRA_SEARCH_DIALOG_OPERATION_NAME,
	});
};

const mapJiraSearchResults = (
	response: JiraSearchData,
): SearchResult<JiraExtendedSearchResult>[] => {
	if (!response?.search?.results?.edges) {
		return [];
	}

	return response.search.results.edges.map((edge) => {
		const node = edge.node;
		const itemId = node.id?.split('/').pop() || node.id;

		if (node.issue) {
			return {
				id: itemId,
				url: node.issue?.webUrl || node.url,
				title: node.issue?.key + ' ' + node.title,
				description: node.description || '',
				type: node.type,
				icon: node.issue?.issueTypeAvatarUrl || '',
				meta: node.issue?.projectField?.project?.name || '',
				additionalMeta: node.lastModifiedDate ? dateFormatter(node.lastModifiedDate) : undefined,
				issueKey: node.issue?.key,
				projectName: node.issue?.projectField?.project?.name,
				projectId: node.issue?.projectField?.project?.id,
				issueType: node.issue?.issueTypeField?.issueType?.name,
				issueTypeId: node.issue?.issueTypeField?.issueType?.issueTypeId,
				status: node.issue?.status?.statusCategory?.name,
				statusColor: node.issue?.status?.statusCategory?.colorName,
				lastModifiedDate: node.lastModifiedDate,
				useHydratedFields: node.useHydratedFields,
				iconUrl: node.issue?.issueTypeAvatarUrl,
			};
		}

		return {
			id: itemId,
			url: node.url,
			title: node.title,
			description: node.description || '',
			type: node.type,
			icon: node.iconUrl || '',
			meta: '',
		};
	});
};

const mapProjectIdsToARIs = ({
	projectIds,
	cloudId,
}: {
	projectIds: string[];
	cloudId: string;
}): string[] => {
	return projectIds.map((projectId) => {
		if (projectId.startsWith('ari:cloud:jira:')) {
			return projectId;
		}
		return `ari:cloud:jira:${cloudId}:project/${projectId}`;
	});
};

const mapAccountIdsToARIs = (accountIds: string[]): string[] => {
	return accountIds.map((accountId) => {
		if (accountId.startsWith(USER_ARI_PREFIX)) {
			return accountId;
		}
		return `${USER_ARI_PREFIX}${accountId}`;
	});
};

const mapAssigneeIdsToARIs = (accountIds: string[]): string[] => {
	return mapAccountIdsToARIs(accountIds);
};

const mapStatusCategoryIdsToEnumValues = (statusCategoryIds: string[]): string[] => {
	return statusCategoryIds
		.map((categoryId) => {
			switch (categoryId) {
				case 'open':
					return 'OPEN';
				case 'done':
					return 'DONE';
				case 'OPEN':
					return 'OPEN';
				case 'DONE':
					return 'DONE';
				default:
					return '';
			}
		})
		.filter(Boolean);
};

const mapLastModifiedFilter = (filters: GenericFilter[]) => {
	const lastModifiedFilter = filters.find((filter) => filter['@type'] === 'lastModified');

	if (!lastModifiedFilter?.from && !lastModifiedFilter?.to) {
		return null;
	}

	return {
		gt: lastModifiedFilter.from as string,
		lt: lastModifiedFilter.to as string,
	};
};

const mapReportedByFilter = (filters: GenericFilter[]) => {
	const reportedByFilter = filters.find((filter) => filter['@type'] === 'reportedBy');

	if (!reportedByFilter || !('accountIds' in reportedByFilter)) {
		return [];
	}

	const accountIds = reportedByFilter.accountIds as string[];
	return mapAccountIdsToARIs(accountIds);
};

const mapLabelFilter = (filters: GenericFilter[]) => {
	const labelFilter = filters.find((filter) => filter['@type'] === 'issueLabels');

	if (!labelFilter || !('issueLabels' in labelFilter)) {
		return [];
	}

	const labels = labelFilter.issueLabels as string[];
	return labels || [];
};

const mapScopesToEntities = (scopes: string[]): string[] => {
	const entityMap: Record<string, EntityATI> = {
		issue: 'ati:cloud:jira:issue',
		project: 'ati:cloud:jira:project',
		board: 'ati:cloud:jira:board',
		filter: 'ati:cloud:jira:filter',
		plan: 'ati:cloud:jira:plan',
	};

	return scopes.flatMap((scope) => {
		// Extract the part after 'jira.' prefix, split by comma, and map to entity ATI types
		const entityKeys = scope.replace('jira.', '').split(',');
		return entityKeys.map((key) => entityMap[key]).filter(Boolean);
	});
};

const createGraphQLSearchSupplier =
	<GraphQLResponse, Scope = string, ExtendedSearchResult = {}>({
		cloudId,
		sections,
		searchSessionId,
		sessionId,
		experience,
		sourceProduct,
		experimentConfig,
		productEdition,
		productDataRegions,
		isJiraIssueIndexed,
		onError,
	}: CreateGraphQLSearchSupplierParams<GraphQLResponse, Scope, ExtendedSearchResult>) =>
	async ({
		query,
		filters: allFilters,
		workspaces,
		sectionIds: allPermissionedScopes,
	}: PostQuerySupplierArgs) => {
		const defaultSites = workspaces?.slice(0, MAX_DEFAULT_SITES).map((workspace) => workspace.id);

		const siteFilter = allFilters.find((filter) => filter['@type'] === SITE_FILTER_TYPE_NAME);
		// @ts-ignore - typescript is not smart enough to know `siteFilter` will only ever be the sites filter
		const sites = siteFilter?.values || defaultSites || [];

		const filters = allFilters
			.filter((filter) => filter['@type'] !== SITE_FILTER_TYPE_NAME)
			.map((f) => f as GenericFilter);

		const projectFilter = allFilters.find((filter) => filter['@type'] === 'projects');
		const projectIds =
			projectFilter && 'projectIds' in projectFilter
				? (projectFilter.projectIds as string[]) || []
				: [];

		const projectARIs = mapProjectIdsToARIs({ projectIds, cloudId });

		const assigneeFilter = allFilters.find((filter) => filter['@type'] === 'assignees');
		const assigneeAccountIds =
			assigneeFilter && 'accountIds' in assigneeFilter
				? (assigneeFilter.accountIds as string[]) || []
				: [];

		const assigneeARIs = mapAssigneeIdsToARIs(assigneeAccountIds);

		const statusFilter = allFilters.find(
			(filter) => filter['@type'] === 'binary_status_categories',
		);
		const statusCategoryIds =
			statusFilter && 'binaryStatusCategories' in statusFilter
				? (statusFilter.binaryStatusCategories as string[]) || []
				: [];

		const statusCategories = mapStatusCategoryIdsToEnumValues(statusCategoryIds);
		const permittedSections = sections
			.filter((section) => allPermissionedScopes.indexOf(section.id) >= 0)
			.filter((section) => (section.isEnabled ? section.isEnabled(query, filters) : true));

		const lastModifiedFilter = mapLastModifiedFilter(filters);
		const reporterARIs = mapReportedByFilter(filters);
		const issueLabels = mapLabelFilter(filters);

		// Create parallel queries for each section scope
		const sectionQueries = permittedSections.map(async (section) => {
			const scopes: Scope[] = [section.scope];
			const mappedEntities = mapScopesToEntities(scopes as string[]);
			// Get experiment layers for this section's entities
			let backendExperiment: BackendExperiment;
			const jiraEnableQsExpLayers = fg('jira_enable_qs_exp_layers');
			if (experimentConfig && mappedEntities.length > 0 && jiraEnableQsExpLayers) {
				try {
					backendExperiment = getBackendExperiment({
						experimentConfig: experimentConfig,
						entities: mappedEntities,
						isHello: cloudId === HELLO_CLOUD_ID,
						productEdition,
						productDataRegions,
						experience: QUICK_SEARCH_EXPERIENCE,
						isJiraIssueIndexed,
					});
				} catch (error) {
					onError?.(error as Error);
					backendExperiment = undefined;
				}
			}

			const variables: JiraSearchDialogQueryVariables = {
				experience,
				query,
				queryVersion: 1,
				cloudIdARI: `ari:cloud:jira::site/${cloudId}`,
				tenantId: cloudId,
				limit: 10,
				commonFilters: {
					range: lastModifiedFilter ? { lastModified: lastModifiedFilter } : {},
				},
				jiraFilters: {
					issueFilter: {
						projectARIs,
						assigneeARIs,
						issueTypeIDs: [],
						statusCategories,
						reporterARIs,
						issueLabels,
					},
					projectFilter: {
						projectTypes: [],
					},
				},
				entities: mappedEntities,
				experimentLayers:
					backendExperiment?.experimentLayers?.map(
						({ analyticsDefinitions: _, ...rest }) => rest,
					) || [],
				sort: [],
				searchSessionId,
				sessionId,
				sourceProduct,
				includeSearchResultJiraPlan: fg('enable_jira_plan_gql'),
			};

			try {
				const response = await jiraSearchDialogQuery({ variables });
				return {
					section,
					response,
					success: true,
					experimentLayers: backendExperiment?.experimentLayers,
				};
			} catch (error) {
				return {
					section,
					response: null,
					success: false,
					experimentLayers: backendExperiment?.experimentLayers,
				};
			}
		});

		try {
			const sectionResults = await Promise.all(sectionQueries);

			const results = sectionResults
				.flatMap((result) => (result.success && result.response?.data ? [result] : []))
				.reduceRight(
					(allSearchItems, searchItem): SearchItems => {
						const { section, response, experimentLayers } = searchItem;
						// There is a bug on Jira Search dialog query where response?.data?.search?.results.totalCount can be unreliable
						// so response.data?.search?.results.edges.length is used as a fallback
						//  https://atlassian.slack.com/archives/C0919PNMSP6/p1756437134267599?thread_ts=1756430740.125979&cid=C0919PNMSP6
						const size =
							response.data?.search?.results.totalCount ||
							response.data?.search?.results.edges.length ||
							0;

						return {
							sections: [
								{
									...section,
									id: section.id,
									title: section.title,
									size,
									searchResults: response?.data ? mapJiraSearchResults(response.data) : [],
									resultMapper: section.resultMapper,
								},
								...allSearchItems.sections, // Added at the end from using reduceRight
							],
							// Take the first defined experiment layer within the sections
							experimentLayers: experimentLayers || allSearchItems.experimentLayers,
							size: (allSearchItems.size ?? 0) + size,
						};
					},
					{
						sections: [],
						experimentLayers: [],
						size: 0,
					},
				);

			return {
				size: results.size,
				sections: results.sections,
				experimentLayers: results.experimentLayers,
			} satisfies SearchItems;
		} catch (error) {
			return { size: 0, sections: [], experimentLayers: [] } satisfies SearchItems;
		}
	};

export const useJiraGraphQLSupplier = <
	GraphQLResponse,
	Scope = string,
	ExtendedSearchResult = object,
>({
	searchSessionId,
	sessionId,
	sections,
	cloudId = '',
	productEdition,
	productDataRegions,
	experimentConfig,
	isJiraIssueIndexed,
}: UseJiraGraphQLSupplierParams<GraphQLResponse, Scope, ExtendedSearchResult>): ResultSuppliers => {
	const productContext = useProductContext();
	const { getPrimaryProduct } = productContext;
	const primaryProduct = getPrimaryProduct();
	const primaryProductId = primaryProduct?.id;
	const { fireAnalyticsEvent } = useAnalytics();

	const onError = () => {
		fireAnalyticsEvent(onGetBackendExperimentFailed());
	};

	const search = createGraphQLSearchSupplier({
		cloudId,
		sections,
		searchSessionId,
		sessionId,
		experience: 'product-search-dialog',
		sourceProduct: primaryProductId || '',
		experimentConfig,
		productEdition,
		productDataRegions,
		isJiraIssueIndexed,
		onError,
	});

	return {
		preQueryItemSupplier: (args) => search({ ...args, query: '', filters: [] }),
		postQueryItemSupplier: search,
	};
};
